import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE = 'https://fifty-two-in-the-lou-production.up.railway.app';

const StarRating = ({ value, onChange, theme }) => (
  <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
    {[1, 2, 3, 4, 5].map(star => (
      <span
        key={star}
        onClick={() => onChange(star)}
        style={{
          fontSize: '1.6rem',
          cursor: 'pointer',
          color: star <= value ? '#EEC170' : theme.border,
          transition: 'color 0.15s'
        }}
      >★</span>
    ))}
  </div>
);

const Section = ({ title, children, theme }) => (
  <div style={{
    backgroundColor: theme.background,
    borderRadius: '10px',
    padding: '16px',
    marginBottom: '16px',
    border: `1px solid ${theme.border}`
  }}>
    <p style={{
      color: '#C0392B',
      fontWeight: '800',
      fontSize: '0.85rem',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      margin: '0 0 12px 0'
    }}>{title}</p>
    {children}
  </div>
);

const BetaPage = () => {
  const { user, getToken } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    anonymous: false,
    name: '',
    suggestionTitle: '',
    suggestionDescription: '',
    accountEaseRating: 0,
    accountChecklistRating: 0,
    accountSchedulingRating: 0,
    accountNotificationsRating: 0,
    accountAddManuallyRating: 0,
    accountThemesRating: 0,
    guestAccessRating: 0,
    guestChecklistRating: 0,
    wouldCreateAccount: null,
    overallDesignRating: 0,
    mobileVsDesktop: '',
    visualIssues: '',
    wouldShare: null,
    shareMethods: [],
    favoriteFeature: '',
    improvements: '',
    bugsEncountered: '',
    additionalComments: '',
  });

  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { redirectTo: '/beta' } });
    }
  }, [user, navigate]);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const toggleShareMethod = (method) => {
    setForm(prev => ({
      ...prev,
      shareMethods: prev.shareMethods.includes(method)
        ? prev.shareMethods.filter(m => m !== method)
        : [...prev.shareMethods, method]
    }));
  };

  const handleSubmit = async () => {
    if (!form.suggestionTitle) {
      setError('Please enter a suggestion title.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const token = JSON.parse(sessionStorage.getItem('user'))?.token;
      const payload = {
        ...form,
        shareMethods: form.shareMethods.join(','),
      };
      await axios.post(`${API_BASE}/api/beta/submit`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmitted(true);
    } catch (e) {
      console.error(e);
      setError('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    marginBottom: '12px',
    border: `2px solid ${theme.border}`,
    borderRadius: '8px',
    backgroundColor: theme.cardBackground,
    color: theme.text,
    fontSize: '0.9rem',
    boxSizing: 'border-box',
    outline: 'none',
    fontFamily: 'inherit',
  };

  const label = (txt) => (
    <p style={{ color: theme.subText, fontSize: '0.85rem', fontWeight: '600', margin: '0 0 4px 0' }}>
      {txt}
    </p>
  );

  const yesno = (key) => (
    <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
      {['Yes', 'No'].map(opt => (
        <button
          key={opt}
          onClick={() => set(key, opt === 'Yes')}
          style={{
            flex: 1, padding: '10px', borderRadius: '8px', border: 'none',
            backgroundColor: form[key] === (opt === 'Yes') ? '#C0392B' : theme.border,
            color: form[key] === (opt === 'Yes') ? '#fff' : theme.text,
            fontWeight: '700', cursor: 'pointer', fontSize: '0.9rem'
          }}
        >{opt}</button>
      ))}
    </div>
  );

  if (!user) return null;

  if (submitted) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.background,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          backgroundColor: theme.cardBackground,
          borderRadius: '16px',
          padding: '40px 30px',
          maxWidth: '420px',
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
        }}>
          <p style={{ fontSize: '3rem', marginBottom: '16px' }}>🎉</p>
          <h2 style={{ color: '#C0392B', fontWeight: '800', marginBottom: '12px', fontSize: '1.3rem' }}>
            Thank You!
          </h2>
          <p style={{ color: theme.text, fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '8px' }}>
            We received your feedback and we truly appreciate you taking the time.
          </p>
          <p style={{ color: theme.subText, fontSize: '0.85rem', lineHeight: '1.6', marginBottom: '24px' }}>
            Your suggestions help shape the future of 52 in the Lou. A confirmation has been sent to your email.
          </p>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '12px 24px', borderRadius: '8px', border: 'none',
              backgroundColor: '#C0392B', color: '#fff',
              fontWeight: '700', cursor: 'pointer', fontSize: '0.9rem'
            }}
          >
            Back to Checklist
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: theme.background }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#C0392B',
        padding: '16px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <button onClick={() => navigate('/')} style={{
          background: 'none', border: 'none', color: '#fff',
          fontSize: '1.2rem', cursor: 'pointer'
        }}>←</button>
        <h1 style={{ color: '#EEC170', fontWeight: '800', fontSize: '1.1rem', margin: 0 }}>
          Beta Feedback
        </h1>
        <div style={{ width: '24px' }} />
      </div>

      <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
        <p style={{ color: theme.subText, fontSize: '0.85rem', marginBottom: '20px', textAlign: 'center' }}>
          You're part of the first wave. Your feedback directly shapes what gets built next.
        </p>

        {error && (
          <p style={{ color: '#ef4444', fontSize: '0.85rem', textAlign: 'center', marginBottom: '12px' }}>
            {error}
          </p>
        )}

        {/* IDENTITY */}
        <Section title="Your Identity" theme={theme}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <input
              type="checkbox"
              id="anon"
              checked={form.anonymous}
              onChange={e => set('anonymous', e.target.checked)}
              style={{ accentColor: '#C0392B', width: '16px', height: '16px' }}
            />
            <label htmlFor="anon" style={{ color: theme.text, fontSize: '0.9rem', cursor: 'pointer' }}>
              Submit anonymously
            </label>
          </div>
          {!form.anonymous && (
            <>
              {label('Your Name')}
              <input
                placeholder="First and last name"
                value={form.name}
                onChange={e => set('name', e.target.value)}
                style={inputStyle}
              />
            </>
          )}
        </Section>

        {/* SUGGESTION */}
        <Section title="Your Suggestion" theme={theme}>
          {label('Suggestion Title *')}
          <input
            placeholder="Give your suggestion a title"
            value={form.suggestionTitle}
            onChange={e => set('suggestionTitle', e.target.value)}
            style={inputStyle}
          />
          {label('Suggestion Description')}
          <textarea
            placeholder="Describe your suggestion in detail..."
            value={form.suggestionDescription}
            onChange={e => set('suggestionDescription', e.target.value)}
            style={{ ...inputStyle, minHeight: '90px', resize: 'vertical' }}
          />
        </Section>

        {/* ACCOUNT MODE */}
        <Section title="Account Mode — Rate Your Experience" theme={theme}>
          {[
            ['accountEaseRating', 'How easy was it to create an account?'],
            ['accountChecklistRating', 'How intuitive is the checklist?'],
            ['accountSchedulingRating', 'How useful is the scheduling feature?'],
            ['accountNotificationsRating', 'How useful are push notifications?'],
            ['accountAddManuallyRating', 'How useful is Add Manually?'],
            ['accountThemesRating', 'How easy is it to navigate themes?'],
          ].map(([key, lbl]) => (
            <div key={key}>
              {label(lbl)}
              <StarRating value={form[key]} onChange={v => set(key, v)} theme={theme} />
            </div>
          ))}
        </Section>

        {/* GUEST MODE */}
        <Section title="Guest Mode — Rate Your Experience" theme={theme}>
          {label('How easy was it to access guest mode?')}
          <StarRating value={form.guestAccessRating} onChange={v => set('guestAccessRating', v)} theme={theme} />
          {label('How useful was the guest checklist experience?')}
          <StarRating value={form.guestChecklistRating} onChange={v => set('guestChecklistRating', v)} theme={theme} />
          {label('Would you create an account after trying guest mode?')}
          {yesno('wouldCreateAccount')}
        </Section>

        {/* UI/UX */}
        <Section title="UI / UX" theme={theme}>
          {label('Overall design rating')}
          <StarRating value={form.overallDesignRating} onChange={v => set('overallDesignRating', v)} theme={theme} />
          {label('How do you primarily use the app?')}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            {['Mobile', 'Desktop', 'Both'].map(opt => (
              <button
                key={opt}
                onClick={() => set('mobileVsDesktop', opt)}
                style={{
                  flex: 1, padding: '10px', borderRadius: '8px', border: 'none',
                  backgroundColor: form.mobileVsDesktop === opt ? '#C0392B' : theme.border,
                  color: form.mobileVsDesktop === opt ? '#fff' : theme.text,
                  fontWeight: '700', cursor: 'pointer', fontSize: '0.85rem'
                }}
              >{opt}</button>
            ))}
          </div>
          {label('Any visual issues or complaints?')}
          <textarea
            placeholder="Describe any visual bugs, layout issues, or design complaints..."
            value={form.visualIssues}
            onChange={e => set('visualIssues', e.target.value)}
            style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }}
          />
        </Section>

        {/* SHARING */}
        <Section title="Sharing" theme={theme}>
          {label('Would you share this app with a friend?')}
          {yesno('wouldShare')}
          {label('How would you prefer to share it? (select all that apply)')}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
            {['Text', 'Email', 'Instagram', 'TikTok', 'LinkedIn', 'Word of Mouth'].map(method => (
              <button
                key={method}
                onClick={() => toggleShareMethod(method)}
                style={{
                  padding: '8px 14px', borderRadius: '20px', border: 'none',
                  backgroundColor: form.shareMethods.includes(method) ? '#C0392B' : theme.border,
                  color: form.shareMethods.includes(method) ? '#fff' : theme.text,
                  fontWeight: '600', cursor: 'pointer', fontSize: '0.82rem'
                }}
              >{method}</button>
            ))}
          </div>
        </Section>

        {/* OPEN ENDED */}
        <Section title="Open Ended" theme={theme}>
          {label("What's your favorite feature?")}
          <textarea
            placeholder="Tell us what you love most..."
            value={form.favoriteFeature}
            onChange={e => set('favoriteFeature', e.target.value)}
            style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }}
          />
          {label('What would you improve?')}
          <textarea
            placeholder="What would make this app better?"
            value={form.improvements}
            onChange={e => set('improvements', e.target.value)}
            style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }}
          />
          {label('Any bugs you encountered?')}
          <textarea
            placeholder="Describe any bugs or unexpected behavior..."
            value={form.bugsEncountered}
            onChange={e => set('bugsEncountered', e.target.value)}
            style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }}
          />
          {label('Additional comments')}
          <textarea
            placeholder="Anything else you'd like us to know..."
            value={form.additionalComments}
            onChange={e => set('additionalComments', e.target.value)}
            style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }}
          />
        </Section>

        {/* SUBMIT */}
        <button
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            width: '100%', padding: '14px', borderRadius: '10px', border: 'none',
            backgroundColor: submitting ? theme.border : '#C0392B',
            color: submitting ? theme.subText : '#fff',
            fontWeight: '800', cursor: submitting ? 'not-allowed' : 'pointer',
            fontSize: '1rem', marginBottom: '40px'
          }}
        >
          {submitting ? 'Submitting...' : 'Submit Feedback'}
        </button>
      </div>
    </div>
  );
};

export default BetaPage;